import { CLAIM_KEYS } from '../../authorization/constants/authorization.constants';

/**
 * User profile information extracted from customAccessToken claims after successful authentication. Provides
 * comprehensive user identity and tenant context for authenticated sessions.
 */
export interface UserProfile {
  /** Unique user identifier within the identity system. */
  userId?: string;
  /** User's display name or login username. */
  userName?: string;
  /** User's email address. */
  userEmail?: string;
  /** Primary client/application identifier associated with the user's role or access. */
  primaryClientId?: string;
  /** User's role identifier within the tenant. */
  roleId?: string;
  /** Client/application identifier from the token claims. */
  clientId?: string;
  /** Vendor or organization identifier. */
  vendorId?: string;
}

/** Creates an empty user profile for signed-out sessions. */
export function createEmptyUserProfile(): UserProfile {
  return {};
}

/**
 * Creates a user profile with values extracted from token claims.
 *
 * @param claims - Token claims record to extract from
 * @returns UserProfile with extracted values
 */
export function createUserProfile(claims: Record<string, unknown>): UserProfile {
  return {
    userId: extractStringClaim(claims, CLAIM_KEYS.SUBJECT),
    userName: extractStringClaim(claims, CLAIM_KEYS.USER_NAME),
    userEmail: extractStringClaim(claims, CLAIM_KEYS.USER_EMAIL),
    primaryClientId: extractStringClaim(claims, CLAIM_KEYS.PRIMARY_CLIENT_ID),
    roleId: extractStringClaim(claims, CLAIM_KEYS.ROLE_ID),
    clientId: extractStringClaim(claims, CLAIM_KEYS.CLIENT_ID),
    vendorId: extractStringClaim(claims, CLAIM_KEYS.VENDOR_ID),
  };
}

/**
 * Safely extracts a string claim from token claims.
 *
 * @param claims - Token claims record
 * @param key - Claim key to extract
 * @returns The string value, or undefined if not found or not a string
 */
function extractStringClaim(claims: Record<string, unknown>, key: string): string | undefined {
  const value = claims[key];
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return undefined;
}
